/**
 * The fleet view — the screen this product is actually used through.
 *
 * ── Two groupings, one set of verdicts (decision 2, 2026-09-15) ──────────
 *
 * It opens as a DEVICE TABLE, worst first: one row per asset, the status pill
 * leading, the reason beside it, sortable by anything. That is the screen an
 * operator triages from — fourteen faults are fourteen rows at the top, not
 * fourteen cells scattered across the site cards they happen to live in.
 *
 * The site cards below are the other grouping, one tab away (`#/fleet/sites`).
 * Both read the same verdicts, counters and filter, so the two cannot disagree
 * about a camera. What the table would otherwise lose — that a whole site is
 * dark, which is one failure and not N camera faults — is said above it and
 * on every row at that site.
 *
 * Recreates the predecessor's grid: sites, their assets, one cell each,
 * coloured by current state. Three deliberate differences, all fixes rather
 * than redesigns:
 *
 *   1. A cell says WHY. The old grid's colour was the whole message, and an
 *      operator had to open the card to learn what was wrong.
 *   2. There is a sixth colour: "not checkable". An asset whose driver cannot
 *      answer a question is not healthy and not faulty, and the old grid had
 *      nowhere to put that so it painted it green.
 *   3. Sites with something wrong are at the top, and each one says what in
 *      words. See below — this is the part the predecessor got right and site
 *      grouping threw away.
 *
 * ── 🔴 Rising is not filtering ────────────────────────────────────────────
 *
 * The predecessor's landing page showed ONLY problem cameras. That is what made
 * it scannable and it is also the part not to reproduce: the healthy fleet was
 * unreachable from the screen that existed to show the fleet, and "347 cameras"
 * was quoted for months against 283 actually polled because no surface ever
 * drew the other 64.
 *
 * So every site is here. The ranked ones are first, the quiet ones are behind a
 * disclosure that names how many there are, and nothing is dropped. The order
 * itself is the server's — see the `/api/fleet` query, which computes it in SQL
 * so it survives the pagination this list will need somewhere north of 190
 * sites.
 *
 * The counters at the top are also filters. Pressing one narrows the cards to
 * the assets in that state; pressing it again shows everything. That is the
 * faults-first screen the owner asked for, reached without a second control —
 * and it is a FILTER the operator applied and can see applied, which is a
 * different thing from a screen that hides the healthy fleet by design.
 */

import { ArrowLeft, Download, Search } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';

import type {
  AlertRow,
  Asset,
  CheckStateRow,
  Fleet as FleetData,
  FleetSite,
  SnapshotMeta,
} from './api.ts';
import {
  Banner,
  Button,
  DataTable,
  DownloadLink,
  Empty,
  Pill,
  Section,
  Stat,
  StatRow,
  Toolbar,
  type ColumnDef,
} from './components/index.ts';
import {
  CELL_LABELS,
  fleetCellState,
  needingAttention,
  scopeFleetToSite,
  siteOrderKey,
  STATE_RANK,
  summariseSite,
  worstFirst,
  type AssetChecks,
  type CellState,
  type CellVerdict,
  type SiteSummary,
} from './fleet-state.ts';
import { KindPicture } from './KindPicture.tsx';


/**
 * What NX calls this camera, when that says something the card does not
 * already: a camera linked to NX (070) keeps its CosMon name, and the site
 * knows it by the NX one ("C06 - Solar Pole #2").
 */
function nxAlias(asset: Asset): string | null {
  const nx = asset.nx_name ?? null;
  if (nx === null || nx === '' || nx === asset.name || nx === asset.code) return null;
  // Its NX name and what NX says of it: the card's colour is CosMon's
  // verdict, and this is NX's word, said as NX's.
  return asset.nx_status !== undefined && asset.nx_status !== null ? `${nx} · ${asset.nx_status}` : nx;
}
/** NX failed the camera over to another server (072): said until NX moves it back. */
function nxFailover(asset: Asset): string | null {
  const home = asset.nx_failover_home ?? null;
  return home === null ? null : `On a failover NX server (normally ${home})`;
}

export type FleetGrouping = 'devices' | 'sites';

interface Props {
  fleet: FleetData;
  /** Which of the two screens: the device table (default) or the site cards. */
  grouping: FleetGrouping;
  /** Offer the asset export. Owners and admins; the API refuses anyone else. */
  canExport?: boolean;
  onSelectAsset: (assetId: string) => void;
  /** One site's overview: counters, card and devices for that site alone. */
  siteId?: string;
  /** Open a site's overview — the site cards' titles link to it. */
  onOpenSite?: (siteId: string) => void;
  /** Back from a site's overview to every site. */
  onLeaveSite?: () => void;
}

function checksByAsset(rows: CheckStateRow[]): Map<string, AssetChecks> {
  const map = new Map<string, AssetChecks>();
  for (const row of rows) {
    const existing = map.get(row.asset_id) ?? {};
    existing[row.check_key] = { outcome: row.outcome, valueText: row.value_text, reason: row.reason };
    map.set(row.asset_id, existing);
  }
  return map;
}

function alertsByAsset(rows: AlertRow[]): Map<string, AlertRow[]> {
  const map = new Map<string, AlertRow[]>();
  for (const row of rows) {
    map.set(row.asset_id, [...(map.get(row.asset_id) ?? []), row]);
  }
  return map;
}

const LEGEND_ORDER: CellState[] = ['ok', 'fault', 'warning', 'unmonitored', 'stale', 'maintenance'];

/** An age an operator can read. */
function ago(iso: string | null): string {
  if (iso === null) return 'never';
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 48) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

/** A site the server put above the fold. `rank_dark` implies severity 2. */
function needsAttention(site: FleetSite): boolean {
  return !site.rank_placeless && site.rank_severity > 0;
}

/**
 * The server's order, held still while the same sites are in trouble.
 *
 * 🔴 An operator reads this list top-down and scrolls. Re-sorting on every poll
 * costs them their place for no gain: proportions move as one camera at a
 * twelve-camera site recovers and comes back, and each move slides every site
 * below it. The set of sites with something open changes far more slowly than
 * the numbers inside them do.
 *
 * So a new order is adopted only when the MEMBERSHIP changes — a site starts or
 * stops having something open, or is added or retired — or when a site's worst
 * problem does: warning to fault, or every camera dark (`siteOrderKey`). Within an unchanged set
 * the held order stands, and the counts inside each card still update, because
 * these are freshly fetched rows in a remembered order rather than remembered
 * rows.
 *
 * ⚠️ This can leave a site one place lower than its rank for as long as the set
 * holds. That is the trade and it is the right way round: a wholly dark site
 * appearing is a membership change and jumps to the top immediately, which is
 * the case the ranking exists for.
 */
function useSteadyOrder(sites: readonly FleetSite[]): readonly FleetSite[] {
  const held = useRef<{ signature: string; ids: readonly string[] } | null>(null);

  return useMemo(() => {
    const signature = siteOrderKey(sites);

    const previous = held.current;
    if (previous !== null && previous.signature === signature) {
      const byId = new Map(sites.map((s) => [s.id, s]));
      const kept = previous.ids.flatMap((id) => {
        const site = byId.get(id);
        return site === undefined ? [] : [site];
      });
      if (kept.length === sites.length) return kept;
    }

    held.current = { signature, ids: sites.map((s) => s.id) };
    return sites;
  }, [sites]);
}

function plural(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? '' : 's'}`;
}

export function FleetView({
  fleet: whole,
  grouping,
  canExport = false,
  onSelectAsset,
  siteId,
  onOpenSite,
  onLeaveSite,
}: Props): React.JSX.Element {
  /*
   * A site's overview is this same screen over a smaller fleet, so its
   * counters, card and table are computed by the code — and with the verdicts
   * — the whole-fleet view uses. Nothing here can disagree with Fleet.
   */
  const fleet = useMemo(() => (siteId === undefined ? whole : scopeFleetToSite(whole, siteId)), [whole, siteId]);
  const checks = useMemo(() => checksByAsset(fleet.checkState), [fleet.checkState]);
  const alerts = useMemo(() => alertsByAsset(fleet.alerts), [fleet.alerts]);

  /**
   * The one active filter, or none. One at a time: "faults" and "warnings"
   * together is a question the counters cannot phrase, and two pressed tiles
   * would leave the reader working out whether that means AND or OR.
   */
  const [filter, setFilter] = useState<CellState | null>(null);

  const verdicts = useMemo(() => {
    const map = new Map<string, CellVerdict>();
    for (const asset of fleet.assets) {
      map.set(
        asset.id,
        fleetCellState({
          lifecycle: asset.lifecycle,
          checks: checks.get(asset.id) ?? {},
          openAlertSeverities: (alerts.get(asset.id) ?? []).map((a) => a.severity),
          // The exemption. Absent this, a timelapse camera with a leftover card
          // paints red here while the alert list correctly holds nothing.
          recordsOnboard: asset.records_onboard,
        }),
      );
    }
    return map;
  }, [fleet.assets, checks, alerts]);

  const counts = useMemo(() => {
    const tally: Partial<Record<CellState, number>> = {};
    for (const verdict of verdicts.values()) {
      tally[verdict.state] = (tally[verdict.state] ?? 0) + 1;
    }
    return tally;
  }, [verdicts]);

  /*
   * Which assets have a frame, and how old it is.
   *
   * Metadata comes down with the fleet so the grid does not fire a request per
   * cell just to discover there is no picture. The bytes are fetched by the
   * <img> itself, which is what browsers are good at.
   */
  const snapshots = useMemo(() => {
    const map = new Map<string, SnapshotMeta>();
    for (const row of fleet.snapshots ?? []) map.set(row.asset_id, row);
    return map;
  }, [fleet.snapshots]);

  /*
   * Each site's assets, worst first. The server sends them by code, which at
   * Sandy Point put a camera not answering at all fourth on the card, under
   * three lesser items — see `KIND_RANK`.
   */
  const bySite = useMemo(() => {
    const map = new Map<string | null, Asset[]>();
    for (const asset of fleet.assets) {
      map.set(asset.site_id, [...(map.get(asset.site_id) ?? []), asset]);
    }
    for (const [siteId, assets] of map) map.set(siteId, worstFirst(assets, verdicts));
    return map;
  }, [fleet.assets, verdicts]);

  /*
   * 🔴 What is wrong at each site, in words.
   *
   * The predecessor's list was scannable because every row on it WAS a problem.
   * A site card is a card whether or not anything is wrong with it, so a colour
   * alone says less than the old screen did — an operator would have to open
   * the card to learn what "amber" meant, which is the exact complaint that
   * made cells say why.
   *
   * Counted from the same verdicts the cells render, so the sentence and the
   * grid under it cannot disagree. Counted over EVERY asset at the site, not the
   * filtered ones: the line describes the site, and a filter that changed it
   * would make "2 of 2 not answering" read as "1 of 1" the moment somebody
   * pressed Faults.
   */
  const summaries = useMemo(() => {
    const pens = new Set(fleet.sites.filter((s) => s.rank_placeless).map((s) => s.id));
    const map = new Map<string, SiteSummary>();
    for (const [siteId, assets] of bySite) {
      if (siteId === null) continue;
      map.set(
        siteId,
        summariseSite(
          assets.flatMap((asset) => {
            const verdict = verdicts.get(asset.id);
            return verdict === undefined ? [] : [{ lifecycle: asset.lifecycle, verdict }];
          }),
          !pens.has(siteId),
        ),
      );
    }
    return map;
  }, [bySite, verdicts, fleet.sites]);

  /*
   * The order is held over the WHOLE list and the filter is applied after it.
   * Filtering first would change the membership signature on every press and
   * throw away the held order, which is the opposite of what the hook is for.
   */
  const ordered = useSteadyOrder(fleet.sites);

  /** The assets a card shows: all of them, or the ones in the pressed state. */
  const shown = (assets: readonly Asset[]): readonly Asset[] =>
    filter === null ? assets : assets.filter((a) => verdicts.get(a.id)?.state === filter);

  /** Under a filter, a card with nothing in that state is not drawn at all. */
  const drawn = (site: FleetSite): boolean =>
    filter === null || shown(bySite.get(site.id) ?? []).length > 0;

  const attention = ordered.filter((s) => needsAttention(s) && drawn(s));
  const quiet = ordered.filter((s) => !s.rank_placeless && !needsAttention(s) && drawn(s));
  const placeless = ordered.filter((s) => s.rank_placeless && drawn(s));
  const unsited = shown(bySite.get(null) ?? []);

  const card = (site: FleetSite): React.JSX.Element => (
    <SiteCard
      key={site.id}
      site={site}
      assets={bySite.get(site.id) ?? []}
      visible={shown(bySite.get(site.id) ?? [])}
      filter={filter}
      summary={summaries.get(site.id)}
      verdicts={verdicts}
      snapshots={snapshots}
      onSelectAsset={onSelectAsset}
      {...(siteId === undefined && onOpenSite !== undefined ? { onOpenSite } : {})}
    />
  );

  const counters = (
    /*
      Counters, not a legend.
      The first version used the legend row to carry the numbers, so six
      swatch-and-label pairs did double duty and the counts read as an
      afterthought. A number an operator scans first should be typeset first
      — and pressed first: each one is the filter for the assets it counts.
    */
    <StatRow>
      {LEGEND_ORDER.map((state) => (
        <Stat
          key={state}
          n={counts[state] ?? 0}
          label={CELL_LABELS[state]}
          state={state}
          active={filter === state}
          onClick={() => setFilter((current) => (current === state ? null : state))}
        />
      ))}
    </StatRow>
  );

  if (siteId !== undefined) {
    const site = fleet.sites[0];
    const back = onLeaveSite !== undefined && (
      <Toolbar>
        <Button size="sm" variant="ghost" icon={<ArrowLeft size={14} />} onClick={onLeaveSite}>
          All sites
        </Button>
      </Toolbar>
    );
    if (site === undefined) {
      return (
        <div>
          {back}
          <Empty
            title="This site cannot be shown"
            body="It is not in this tenant, or it has been retired. Check which tenant you are in, top left."
          />
        </div>
      );
    }
    return (
      <div>
        {back}
        {counters}
        {filter === null && (
          <AttentionSection
            assets={needingAttention(fleet.assets, verdicts)}
            verdicts={verdicts}
            snapshots={snapshots}
            onSelectAsset={onSelectAsset}
          />
        )}
        {card(site)}
        <DevicesTable
          canExport={false}
          fleet={fleet}
          verdicts={verdicts}
          alerts={alerts}
          checks={fleet.checkState}
          filter={filter}
          onClearFilter={() => setFilter(null)}
          onSelectAsset={onSelectAsset}
        />
      </div>
    );
  }

  if (grouping === 'devices') {
    return (
      <div>
        {counters}
        <DevicesTable
          canExport={canExport}
          fleet={fleet}
          verdicts={verdicts}
          alerts={alerts}
          checks={fleet.checkState}
          filter={filter}
          onClearFilter={() => setFilter(null)}
          onSelectAsset={onSelectAsset}
        />
      </div>
    );
  }

  const nothingDrawn =
    filter !== null &&
    attention.length === 0 &&
    quiet.length === 0 &&
    placeless.length === 0 &&
    unsited.length === 0;

  return (
    <div>
      {counters}

      {nothingDrawn && filter !== null && (
        <Empty
          title={`Nothing in ${CELL_LABELS[filter].toLowerCase()}`}
          body="Press the counter again to show every site."
        />
      )}

      {attention.length > 0 && (
        <p className="band">
          {plural(attention.length, 'site')} with something open, worst first
        </p>
      )}
      {attention.map(card)}

      {quiet.length > 0 && (
        /*
         * Folded, not filtered. The count is on the summary so the quiet fleet
         * is a number an operator can see rather than an absence they have to
         * infer — which is precisely how the predecessor lost 64 cameras.
         */
        <details className="fold">
          <summary>{plural(quiet.length, 'site')} with nothing open</summary>
          <div className="fold-body">{quiet.map(card)}</div>
        </details>
      )}

      {placeless.map((site) => (
        <details className="fold" key={site.id}>
          {/*
            ⚠️ The line is on the CLOSED summary on purpose. A holding pen is
            folded because it is not a place to send anybody, not because the
            faults in it stopped counting — a disclosure that said only "53
            assets" would hide three dark cameras behind a number that looks
            like inventory.
          */}
          <summary>
            {site.name} — {plural((bySite.get(site.id) ?? []).length, 'asset')} with nowhere
            recorded
            {(summaries.get(site.id)?.line ?? '') !== '' && ` · ${summaries.get(site.id)?.line}`}
          </summary>
          <div className="fold-body">
            <p className="small muted">
              A holding pen, not a place: the faults here are real and in the alert list, but
              they share no cause and there is nowhere to send anybody.
            </p>
            {card(site)}
          </div>
        </details>
      ))}

      {unsited.length > 0 && (
        <Section title="Not assigned to a site" summary={plural(unsited.length, 'asset')}>
          <div className="fleet-grid">
            {unsited.map((asset) => (
              <button
                key={asset.id}
                type="button"
                className={`cell clickable ${verdicts.get(asset.id)?.state ?? 'stale'}`}
                onClick={() => onSelectAsset(asset.id)}
              >
                <div className="code">{asset.code ?? asset.name}</div>
                {nxAlias(asset) !== null && <div className="small muted">NX: {nxAlias(asset)}</div>}
              {nxFailover(asset) !== null && <div className="small muted">{nxFailover(asset)}</div>}
                <div className="why">{verdicts.get(asset.id)?.reason}</div>
              </button>
            ))}
          </div>
        </Section>
      )}
    </div>
  );
}

/** One asset's cell: picture, code, NX's name for it, and why it is the colour it is. */
function AssetCell({
  asset,
  verdict,
  hasSnapshot,
  onSelectAsset,
}: {
  asset: Asset;
  verdict: CellVerdict | undefined;
  hasSnapshot: boolean;
  onSelectAsset: (assetId: string) => void;
}): React.JSX.Element {
  return (
    <button
      type="button"
      className={`cell clickable ${verdict?.state ?? 'stale'}`}
      onClick={() => onSelectAsset(asset.id)}
    >
      {/* A camera monitoring product whose screens carry no
          pictures is a table of adjectives. */}
      <div className="thumb">
        {hasSnapshot ? (
          <img
            src={`/api/assets/${asset.id}/snapshot`}
            alt={`Latest frame from ${asset.name}`}
            loading="lazy"
          />
        ) : asset.kind === 'camera' ? (
          <span className="thumb-empty small muted">no frame yet</span>
        ) : (
          <KindPicture kind={asset.kind} label={`${asset.kind} ${asset.code ?? asset.name}`} />
        )}
      </div>
      <div className="code">{asset.code ?? asset.name}</div>
      {nxAlias(asset) !== null && <div className="small muted">NX: {nxAlias(asset)}</div>}
              {nxFailover(asset) !== null && <div className="small muted">{nxFailover(asset)}</div>}
      <div className="small muted">
        {asset.kind}
        {asset.driver_id === null ? ' · no driver' : ` · ${asset.driver_id}`}
      </div>
      {/* The colour is not the whole message. */}
      <div className="why">{verdict?.reason}</div>
    </button>
  );
}

/**
 * The top of a site's overview: what is wrong there, before the whole grid.
 * Drawn only when something is, so a healthy site reads as one green grid.
 * Under a counter filter it steps aside: the grid is already narrowed.
 */
function AttentionSection({
  assets,
  verdicts,
  snapshots,
  onSelectAsset,
}: {
  assets: readonly Asset[];
  verdicts: Map<string, CellVerdict>;
  snapshots: Map<string, SnapshotMeta>;
  onSelectAsset: (assetId: string) => void;
}): React.JSX.Element | null {
  if (assets.length === 0) return null;
  return (
    <Section title="Needs attention" summary={plural(assets.length, 'asset')}>
      <div className="fleet-grid">
        {assets.map((asset) => (
          <AssetCell
            key={asset.id}
            asset={asset}
            verdict={verdicts.get(asset.id)}
            hasSnapshot={snapshots.has(asset.id)}
            onSelectAsset={onSelectAsset}
          />
        ))}
      </div>
    </Section>
  );
}

interface SiteCardProps {
  site: FleetSite;
  /** Every asset at the site. The summary line and the count are about these. */
  assets: readonly Asset[];
  /** The ones drawn — all of `assets`, or the ones in the pressed state. */
  visible: readonly Asset[];
  filter: CellState | null;
  summary: SiteSummary | undefined;
  verdicts: Map<string, CellVerdict>;
  snapshots: Map<string, SnapshotMeta>;
  onSelectAsset: (assetId: string) => void;
  /** When given, the title opens the site's overview. */
  onOpenSite?: (siteId: string) => void;
}

function SiteCard({
  site,
  assets,
  visible,
  filter,
  summary,
  verdicts,
  snapshots,
  onSelectAsset,
  onOpenSite,
}: SiteCardProps): React.JSX.Element {
  const line = summary?.line ?? '';

  const lineState: CellState = site.rank_dark
    ? 'fault'
    : site.rank_severity === 2
      ? 'fault'
      : site.rank_severity === 1
        ? 'warning'
        : 'ok';

  const meta = [
    site.code,
    [site.locality, site.region].filter(Boolean).join(', ') || 'no address',
    site.timezone,
    filter === null
      ? plural(assets.length, 'asset')
      : `${visible.length} of ${plural(assets.length, 'asset')} in ${CELL_LABELS[filter].toLowerCase()}`,
  ]
    .filter((part) => part !== null && part !== '')
    .join(' · ');

  const pills = (
    <>
      {site.rank_dark && (
        /*
         * The distinction the whole ranking exists for. A site where every
         * asset in service is dark is ONE failure — power, a router, a link —
         * and dispatching a camera technician to it is the wrong call.
         */
        <Pill
          state="fault"
          reason="Every asset in service here is not answering: one site failure — power, a router, a link — rather than several camera faults."
        >
          whole site dark
        </Pill>
      )}
      {line !== '' && <Pill state={lineState}>{line}</Pill>}
    </>
  );

  return (
    <Section
      title={
        onOpenSite === undefined ? (
          site.name
        ) : (
          <button type="button" className="link-quiet" onClick={() => onOpenSite(site.id)} title="Open this site's overview">
            {site.name}
          </button>
        )
      }
      summary={meta}
      actions={pills}
    >
      {assets.length === 0 ? (
        <Empty title="No assets at this site" />
      ) : (
        <div className="fleet-grid">
          {visible.map((asset) => (
            <AssetCell
              key={asset.id}
              asset={asset}
              verdict={verdicts.get(asset.id)}
              hasSnapshot={snapshots.has(asset.id)}
              onSelectAsset={onSelectAsset}
            />
          ))}
        </div>
      )}
    </Section>
  );
}

// ── The device table ─────────────────────────────────────────────────────

interface DeviceRow {
  asset: Asset;
  verdict: CellVerdict;
  site: FleetSite | null;
  openAlerts: number;
  lastChecked: string | null;
}

interface DevicesTableProps {
  canExport: boolean;
  fleet: FleetData;
  verdicts: Map<string, CellVerdict>;
  alerts: Map<string, AlertRow[]>;
  checks: readonly CheckStateRow[];
  filter: CellState | null;
  onClearFilter: () => void;
  onSelectAsset: (assetId: string) => void;
}

function DevicesTable({
  canExport,
  fleet,
  verdicts,
  alerts,
  checks,
  filter,
  onClearFilter,
  onSelectAsset,
}: DevicesTableProps): React.JSX.Element {
  const [query, setQuery] = useState('');

  const rows = useMemo<DeviceRow[]>(() => {
    const sites = new Map(fleet.sites.map((s) => [s.id, s]));
    const latest = new Map<string, string>();
    for (const row of checks) {
      const seen = latest.get(row.asset_id);
      if (seen === undefined || row.last_checked > seen) latest.set(row.asset_id, row.last_checked);
    }
    return fleet.assets.flatMap((asset) => {
      const verdict = verdicts.get(asset.id);
      if (verdict === undefined) return [];
      return [
        {
          asset,
          verdict,
          site: asset.site_id === null ? null : (sites.get(asset.site_id) ?? null),
          openAlerts: (alerts.get(asset.id) ?? []).length,
          lastChecked: latest.get(asset.id) ?? null,
        },
      ];
    });
  }, [fleet.assets, fleet.sites, verdicts, alerts, checks]);

  const needle = query.trim().toLowerCase();
  const shown = rows.filter((r) => {
    if (filter !== null && r.verdict.state !== filter) return false;
    if (needle === '') return true;
    return [r.asset.code, r.asset.name, r.site?.name, r.site?.code, r.asset.kind, r.asset.driver_id]
      .some((v) => typeof v === 'string' && v.toLowerCase().includes(needle));
  });

  /*
   * 🔴 The one thing a per-device table cannot show by itself. Twelve dark
   * rows at one site read as twelve camera faults; they are one power, router
   * or link failure, and sending a camera technician is the wrong call.
   */
  const darkSites = fleet.sites.filter((s) => s.rank_dark && !s.rank_placeless);

  const columns = useMemo<ColumnDef<DeviceRow, unknown>[]>(
    () => [
      {
        id: 'status',
        header: 'Status',
        // Worst first, and within a state the site with more down first.
        accessorFn: (r) => STATE_RANK[r.verdict.state] * 10_000 - (r.site?.rank_down ?? 0),
        cell: ({ row }) => (
          <Pill state={row.original.verdict.state} reason={row.original.verdict.reason}>
            {CELL_LABELS[row.original.verdict.state]}
          </Pill>
        ),
        size: 132,
      },
      {
        id: 'asset',
        header: 'Asset',
        accessorFn: (r) => (r.asset.code ?? r.asset.name).toLowerCase(),
        cell: ({ row }) => {
          const { asset } = row.original;
          return (
            <div>
              <div className="cell-strong">{asset.code ?? asset.name}</div>
              {asset.code !== null && asset.name !== asset.code && (
                <div className="small muted">{asset.name}</div>
              )}
              {nxAlias(asset) !== null && <div className="small muted">NX: {nxAlias(asset)}</div>}
              {nxFailover(asset) !== null && <div className="small muted">{nxFailover(asset)}</div>}
            </div>
          );
        },
      },
      {
        id: 'why',
        header: 'Why',
        accessorFn: (r) => r.verdict.reason,
        enableSorting: false,
        cell: ({ row }) => <span className="small muted">{row.original.verdict.reason}</span>,
      },
      {
        id: 'site',
        header: 'Site',
        accessorFn: (r) => (r.site?.name ?? '').toLowerCase(),
        cell: ({ row }) => {
          const { site } = row.original;
          if (site === null) return <span className="muted">Not assigned</span>;
          return (
            <div className="row-tight">
              <span>{site.name}</span>
              {site.rank_dark && !site.rank_placeless && (
                <Pill state="fault" reason="Every asset in service here is not answering: one site failure, not several camera faults.">
                  site dark
                </Pill>
              )}
            </div>
          );
        },
      },
      {
        id: 'alerts',
        header: 'Open',
        accessorFn: (r) => r.openAlerts,
        cell: ({ row }) =>
          row.original.openAlerts === 0 ? (
            <span className="faint">—</span>
          ) : (
            <Pill count>{row.original.openAlerts}</Pill>
          ),
        meta: { align: 'right' },
        size: 72,
      },
      {
        id: 'kind',
        header: 'Kind',
        accessorFn: (r) => `${r.asset.kind} ${r.asset.driver_id ?? ''}`,
        cell: ({ row }) => (
          <span className="small muted">
            {row.original.asset.kind}
            {row.original.asset.driver_id === null ? ' · no driver' : ` · ${row.original.asset.driver_id}`}
          </span>
        ),
      },
      {
        id: 'checked',
        header: 'Last checked',
        accessorFn: (r) => r.lastChecked ?? '',
        cell: ({ row }) => <span className="small muted">{ago(row.original.lastChecked)}</span>,
        size: 112,
      },
    ],
    [],
  );

  return (
    <>
      {darkSites.length > 0 && (
        <Banner tone="fault" title={`${plural(darkSites.length, 'site')} entirely dark`}>
          {darkSites.map((s) => s.name).join(', ')}. Every asset in service there is not answering, which
          is one power, router or link failure per site rather than a camera fault on each.
        </Banner>
      )}

      <Toolbar
        end={
          canExport ? (
            <DownloadLink href="/api/export/assets.csv" icon={<Download size={14} />}>
              Export CSV
            </DownloadLink>
          ) : undefined
        }
      >
        <label className="search-field">
          <Search size={14} aria-hidden="true" />
          <input
            type="search"
            placeholder="Filter by code, name, site or driver"
            aria-label="Filter devices"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </label>
        <span className="small muted">
          {shown.length === rows.length
            ? plural(rows.length, 'device')
            : `${shown.length} of ${plural(rows.length, 'device')}`}
          {filter !== null && ` · ${CELL_LABELS[filter].toLowerCase()} only`}
        </span>
      </Toolbar>

      <Section flush>
        <DataTable
          data={shown}
          columns={columns}
          getRowId={(r) => r.asset.id}
          initialSort={[{ id: 'status', desc: false }]}
          onRowClick={(r) => onSelectAsset(r.asset.id)}
          rowClass={(r) => (r.verdict.state === 'fault' ? 'row-fault' : undefined)}
          minWidth={880}
          caption="Devices, worst first"
          empty={
            <Empty
              title={filter === null && needle === '' ? 'No devices in this tenant' : 'Nothing matches'}
              action={
                filter !== null ? (
                  <button type="button" className="link-quiet" onClick={onClearFilter}>
                    Show every state
                  </button>
                ) : undefined
              }
            />
          }
        />
      </Section>
    </>
  );
}
